import { useEffect, useState } from "react";
import { StickyNote, X } from "lucide-react";
import { cn } from "@/cn";
import type { SearchNote } from "@/api/notes";
import {
  useCreateNote,
  useDeleteNote,
  useNotes,
  useUpdateNote,
} from "@/hooks/useNotes";

function formatWhen(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const diffMs = Date.now() - t;
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(t).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

function NoteLedger({
  collapsed,
  onClose,
}: {
  collapsed?: boolean;
  onClose?: () => void;
}) {
  const { data: notes = [], isLoading } = useNotes();
  const createNote = useCreateNote();
  const updateNote = useUpdateNote();
  const deleteNote = useDeleteNote();

  const [draft, setDraft] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const editing = editingId
    ? notes.find((n) => n.id === editingId) ?? null
    : null;
  const busy = createNote.isPending || updateNote.isPending;

  useEffect(() => {
    if (editingId && !editing) {
      setEditingId(null);
    }
  }, [editingId, editing]);

  function startEdit(note: SearchNote) {
    setEditingId(note.id);
    setDraft(note.content);
    setError(null);
  }

  function cancelEdit() {
    setEditingId(null);
    setDraft("");
    setError(null);
  }

  async function handleSave() {
    const content = draft.trim();
    if (!content) {
      setError("Write something first");
      return;
    }
    setError(null);
    try {
      if (editingId) {
        await updateNote.mutateAsync({ id: editingId, content });
      } else {
        await createNote.mutateAsync(content);
      }
      setEditingId(null);
      setDraft("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save note");
    }
  }

  async function handleDelete(id: string) {
    try {
      await deleteNote.mutateAsync(id);
      if (editingId === id) cancelEdit();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete");
    }
  }

  return (
    <div
      className={cn(
        "flex min-h-0 flex-col rounded-md border border-border bg-card/60",
        collapsed ? "w-72 shadow-lg" : "",
      )}
    >
      <div className="flex items-center justify-between border-b border-border px-2.5 py-2">
        <div className="flex items-center gap-1.5">
          <StickyNote className="size-3.5 text-muted-foreground" />
          <span className="text-[10px] font-semibold uppercase tracking-wide text-faint">
            Notes
          </span>
        </div>
        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            className="rounded p-0.5 text-muted-foreground hover:bg-muted/20 hover:text-foreground"
            aria-label="Close notes"
          >
            <X className="size-3.5" />
          </button>
        ) : null}
      </div>

      <div className="border-b border-border p-2">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={2}
          placeholder={
            editingId
              ? "Edit note… save promotes it to the top"
              : "Jot a search note…"
          }
          className="w-full resize-none rounded-md border border-border bg-bg px-2 py-1.5 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-brand"
        />
        <div className="mt-1.5 flex items-center justify-between gap-2">
          <span className="truncate text-[10px] text-muted-foreground">
            {editingId ? "Editing · save moves to top" : "General ledger"}
          </span>
          <div className="flex gap-1">
            {editingId && (
              <button
                type="button"
                onClick={cancelEdit}
                className="rounded px-2 py-1 text-[10px] text-muted-foreground hover:bg-muted/20"
              >
                Cancel
              </button>
            )}
            <button
              type="button"
              disabled={busy || !draft.trim()}
              onClick={() => void handleSave()}
              className="rounded bg-brand px-2 py-1 text-[10px] font-medium text-brand-foreground hover:opacity-90 disabled:opacity-40"
            >
              {busy ? "…" : editingId ? "Save" : "Add"}
            </button>
          </div>
        </div>
        {error && (
          <p className="mt-1 text-[10px] text-destructive">{error}</p>
        )}
      </div>

      <div className="max-h-48 min-h-0 overflow-y-auto p-1.5">
        {isLoading && (
          <p className="px-1.5 py-2 text-[10px] text-muted-foreground">
            Loading…
          </p>
        )}
        {!isLoading && notes.length === 0 && (
          <p className="px-1.5 py-2 text-[10px] text-muted-foreground">
            No notes yet. Capture thoughts as you search.
          </p>
        )}
        <ul className="space-y-1">
          {notes.map((note) => {
            const active = note.id === editingId;
            return (
              <li key={note.id}>
                <button
                  type="button"
                  onClick={() => startEdit(note)}
                  className={cn(
                    "w-full rounded-md px-2 py-1.5 text-left transition",
                    active
                      ? "bg-brand/10 ring-1 ring-brand/30"
                      : "hover:bg-muted/20",
                  )}
                >
                  <p className="line-clamp-3 whitespace-pre-wrap text-[11px] text-foreground">
                    {note.content}
                  </p>
                  <div className="mt-1 flex items-center justify-between gap-2">
                    <span className="text-[10px] text-muted-foreground">
                      {formatWhen(note.updatedAt)}
                      {note.updatedAt !== note.createdAt ? " · edited" : ""}
                    </span>
                    <span
                      role="button"
                      tabIndex={0}
                      className="text-[10px] text-muted-foreground hover:text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        void handleDelete(note.id);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          e.stopPropagation();
                          void handleDelete(note.id);
                        }
                      }}
                    >
                      Delete
                    </span>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

export function SidebarNotes({ collapsed }: { collapsed: boolean }) {
  const [open, setOpen] = useState(false);

  if (collapsed) {
    return (
      <div className="relative flex justify-center">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={cn(
            "inline-flex size-8 items-center justify-center rounded-md text-muted-foreground transition hover:bg-surface-hover hover:text-foreground",
            open && "bg-brand-soft text-brand",
          )}
          title="Notes"
          aria-label="Notes"
          aria-expanded={open}
        >
          <StickyNote className="size-4" />
        </button>
        {open && (
          <div className="absolute bottom-0 left-full z-40 ml-2">
            <NoteLedger collapsed onClose={() => setOpen(false)} />
          </div>
        )}
      </div>
    );
  }

  return <NoteLedger />;
}
